import { useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";


import {
  Shield,
  Key,
  Search,
  User
} from "lucide-react";
import { CredentialInterface } from "../Credentials/interfaces/credentialinterface";

interface AdminWindowsConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deviceName?: string;
  deviceId?: string;
  credentials?: CredentialInterface[];
  onConnect?: (deviceId: string, username: string, mode: string, label: string) => void;
}


export default function AdminWindowsConnectDialog({
  open,
  onOpenChange,
  deviceName = "PhanyTest3",
  deviceId = "",
  credentials = [],
  onConnect
}: AdminWindowsConnectDialogProps) {
  const [connectionMode, setConnectionMode] = useState("existingCredentials");
  const [selectedCredential, setSelectedCredential] = useState<string>("");
  const [credentialSearch, setCredentialSearch] = useState("");

  const filteredCredentials = credentials?.filter(cred =>
    cred.credentialUsername.toLowerCase().includes(credentialSearch.toLowerCase()) ||
    cred.credentialLabel.toLowerCase().includes(credentialSearch.toLowerCase())
  );

  const handleSubmit = () => {
    if (connectionMode === "existingCredentials") {
      const selectedCred = credentials.find(
        (cred) => String(cred.id) === selectedCredential
      );

      console.log("Admin Windows Connect submitted:", {
        connectionMode,
        selectedCredentialId: selectedCredential,
        selectedCredentialLabel: selectedCred?.credentialLabel,
        selectedCredentialUsername: selectedCred?.credentialUsername,
      });
      onConnect?.(deviceId, selectedCred?.credentialUsername || "", connectionMode, selectedCred?.credentialLabel || "");
    } else if (connectionMode === "manualCredentials") {
      console.log("Admin Windows Connect submitted:", {
        connectionMode,
        manualUsername: selectedCredential,
      });
      onConnect?.(deviceId, selectedCredential || "", connectionMode, "");
    }

    onOpenChange(false);
  };

  const resetDialog = () => {
    setConnectionMode("existingCredentials");
    setSelectedCredential("");
    setCredentialSearch("");
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetDialog();
      onOpenChange(isOpen);
    }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Connect to {deviceName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">

          {/* Connection Mode Selection */}
          <div className="space-y-4">
            <Label className="text-sm font-medium">Select Connection Method</Label>
            <RadioGroup value={connectionMode} onValueChange={setConnectionMode} className="space-y-3">

              {/* Existing Credential Option */}
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="existingCredentials" id="existingCredentials" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="existingCredentials" className="cursor-pointer">
                    <div className="flex items-center gap-2 mb-1">
                      <Key className="w-4 h-4" />
                      <span className="font-medium">Use Existing Credential</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Connect using pre-configured admin credentials</p>
                  </Label>
                </div>
              </div>

              {/* Manual Option */}
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="manualCredentials" id="manualCredentials" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="manualCredentials" className="cursor-pointer">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4" />
                      <span className="font-medium">Manual Credentials</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Manually specify admin credentials for connection</p>
                  </Label>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Credential Selection (for existing credential mode) */}
          {connectionMode === "existingCredentials" && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <Key className="w-4 h-4" />
                  Select Admin Credential
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative">
                  <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search credentials..."
                    value={credentialSearch}
                    onChange={(e) => setCredentialSearch(e.target.value)}
                    className="text-sm pl-9"
                  />
                </div>

                <div className="max-h-64 overflow-y-auto border rounded-md p-2">
                  <RadioGroup value={selectedCredential} onValueChange={setSelectedCredential} className="space-y-2">
                    {filteredCredentials.map((credential) => (
                      <div key={credential.id} className="flex items-start p-3 space-x-3 border rounded-md hover-elevate">
                        <RadioGroupItem value={String(credential.id)} id={String(credential.id)} className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor={String(credential.id)} className="cursor-pointer">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">{credential.credentialUsername}</span>
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">{credential.credentialLabel}</p>
                          </Label>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>
          )}



          {/* Action Buttons */}
          <div className="flex justify-end pt-4 space-x-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                connectionMode === "existingCredentials" && !selectedCredential
              }
              className="bg-primary hover:bg-primary/90"
            >
              <Shield className="w-4 h-4 mr-1" />
              Connect
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}